import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Compass, Rocket } from 'lucide-react';
import Typed from 'typed.js';
import AOS from 'aos';
import AnimatedBorderButton from '../../components/AnimatedBorderButton';

interface HomeProps {
  language: string;
  showScrollArrow: boolean;
  onOpenContact: () => void;
}

const Home: React.FC<HomeProps> = ({ language, showScrollArrow, onOpenContact }) => {
  const typedRef = useRef<HTMLSpanElement>(null);
  const typedInstance = useRef<Typed | null>(null);

  // State quản lý Onboarding Tour (Chỉ dùng cho Tooltip)
  const [showTour, setShowTour] = useState(false);

  // State for Projects Survey Popup
  const [showProjectsSurvey, setShowProjectsSurvey] = useState(false);
  const [surveyStep, setSurveyStep] = useState(1);
  const [countdown, setCountdown] = useState(15);

  // Close survey when clicking outside (optional, but handled manually for now via close button)

  // 2. Nội dung đa ngôn ngữ cho Tour
  const translations = {
    en: {
      helloIm: "Hello, I'm ",
      name: 'Tai Nguyen',
      subtitle1: 'Bachelor of Information Technology',
      subtitle2: 'From Hue City, Vietnam',
      available: 'Available For Work',
      contactBtn: 'Contact Me',
      viewProjects: 'View Projects →',
      typedStrings: ['Software Engineer', 'Game Developer', 'Unity Developer'],
      university: 'Hue University of Sciences',
      scrollDown: 'SCROLL DOWN TO EXPLORE',
      tourMessage: 'You can contact Tai Nguyen right here. Thank you so much!',
      gotIt: 'Got it',
      surveyGreetingTitle: 'Have a great day!',
      surveyGreetingDesc: 'Would you like to be guided to the section that best matches your interests?',
      surveyNextBtn: 'Yes, let\'s go',
      surveySkipBtn: 'Close',
      surveyBackBtn: 'Back',
      surveyQuestionTitle: 'What interests you the most?',
      surveyQuestionDesc: 'Choose an area you want to explore first.',
      featuredOpt: 'Featured Projects',
      experimentalOpt: 'Experimental Lab',
      step1Of2: 'Step 1 of 2',
      step2Of2: 'Step 2 of 2',
      autoCloseMsg: 'Auto-closes in {n}s'
    },
    vi: {
      helloIm: 'Xin chào, Mình là ',
      name: 'Nguyễn Tài',
      subtitle1: 'Cử nhân Công nghệ Thông tin',
      subtitle2: 'Đến từ Thành phố Huế, Việt Nam',
      available: 'Sẵn sàng làm việc',
      contactBtn: 'Liên Hệ',
      viewProjects: 'Xem Dự Án →',
      typedStrings: ['Kỹ sư phần mềm', 'Nhà Phát Triển Trò Chơi', 'Nhà Phát Triển Unity'],
      university: 'Trường Đại học Khoa học Huế',
      scrollDown: 'CUỘN XUỐNG ĐỂ KHÁM PHÁ',
      tourMessage: 'Bạn có thể liên hệ với Tai Nguyen qua đây nhé. Cảm ơn bạn nhiều!',
      gotIt: 'Đã hiểu',
      surveyGreetingTitle: 'Chúc bạn ngày mới tốt lành!',
      surveyGreetingDesc: 'Bạn có mong muốn mình hướng dẫn bạn đến mục phù hợp không?',
      surveyNextBtn: 'Tiếp tục',
      surveySkipBtn: 'Đóng',
      surveyBackBtn: 'Quay lại',
      surveyQuestionTitle: 'Bạn quan tâm đến mảng nào?',
      surveyQuestionDesc: 'Hãy chọn một không gian dự án mà bạn muốn khám phá ngay.',
      featuredOpt: 'Dự Án Nổi Bật',
      experimentalOpt: 'Phòng Thí Nghiệm',
      step1Of2: 'Bước 1 / 2',
      step2Of2: 'Bước 2 / 2',
      autoCloseMsg: 'Tự động đóng sau {n}s'
    }
  };

  const t = translations[language as keyof typeof translations];

  // 3. Logic kích hoạt Tour sau 3 giây (chỉ lần đầu)
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenContactTour');
    if (!hasSeenTour) {
      const timer = setTimeout(() => {
        setShowTour(true);
      }, 3000); // 3 giây
      
      return () => clearTimeout(timer); // Cleanup timer
    }
  }, []);

  const handleCloseTour = () => {
    setShowTour(false);
    localStorage.setItem('hasSeenContactTour', 'true');
  };
  
  // Logic for Auto-close Survey Countdown
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showProjectsSurvey && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (showProjectsSurvey && countdown === 0) {
      setShowProjectsSurvey(false);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [showProjectsSurvey, countdown]);

  // Reset countdown when step changes
  useEffect(() => {
    if (showProjectsSurvey) {
      setCountdown(15);
    }
  }, [surveyStep, showProjectsSurvey]);


  // Initialize Typed.js
  useEffect(() => {
    if (typedRef.current) {
      if (typedInstance.current) {
        typedInstance.current.destroy();
      }
      
      typedInstance.current = new Typed(typedRef.current, {
        strings: t.typedStrings,
        typeSpeed: 50,
        backSpeed: 30,
        backDelay: 1500,
        loop: true,
      });
    }

    return () => {
      if (typedInstance.current) {
        typedInstance.current.destroy();
      }
    };
       
  }, [language, t.typedStrings]);

  const socialLinks = [
    {
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
      ),
      url: 'https://mail.google.com/mail/?view=cm&fs=1&to=ntainguyen.it@gmail.com',
      label: 'Email'
    },
    {
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
        </svg>
      ),
      url: 'https://linkedin.com/in/ntainguyenit',
      label: 'LinkedIn'
    },
    {
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
        </svg>
      ),
      url: 'https://github.com/ntainguyenit',
      label: 'GitHub'
    },
    {
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
      ),
      url: 'https://t.me/ntainguyenit',
      label: 'Telegram'
    },
  ];


  const scrollToFeatured = () => {
    setShowProjectsSurvey(false);
    setTimeout(() => {
        const featuredSection = document.getElementById('projects');
        if (featuredSection) {
          featuredSection.scrollIntoView({ behavior: 'smooth' });
        }
    }, 100);
  };



  const startSurvey = () => {
    setSurveyStep(1);
    setShowProjectsSurvey(true);
  };

  const scrollToAbout = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-screen relative rounded-2xl py-20 px-6">
      {/* CSS Custom tạo hiệu ứng ripple tinh tế */}
      <style>{`
        @keyframes subtle-ripple {
          0% {
            transform: scale(1);
            opacity: 0.6;
          }
          100% {
            transform: scale(1.25);
            opacity: 0;
          }
        }
        .animate-subtle-ripple {
          animation: subtle-ripple 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        @keyframes popup-appear {
          0% { opacity: 0; transform: translateY(10px) scale(0.95); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-popup-appear {
          animation: popup-appear 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @keyframes fade-up-home {
          0% { opacity: 0; transform: translateY(30px); filter: blur(10px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        .animate-fade-up-home {
          animation: fade-up-home 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>

      <div className="relative flex items-center justify-center min-h-screen pt-10 pb-56 px-6 z-10">
        <div className={`max-w-4xl mx-auto text-center opacity-100`}>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-gray-800 dark:text-gray-200">{t.helloIm}</span>
              <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">{t.name}</span>
            </h1>

            <div className="mb-6 text-2xl md:text-3xl text-gray-600 dark:text-gray-300">
              <div className="mb-2">
                <span ref={typedRef} className="text-blue-600 dark:text-blue-400 font-semibold text-2xl md:text-3xl"></span>
                <div className="flex items-center justify-center mt-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-2 shadow-lg"></div>
                  <span className="text-green-600 dark:text-green-400 text-sm font-medium">{t.available}</span>
                </div>
              </div>
              <div className="mt-4">
                <span className="text-gray-700 dark:text-gray-300 text-xl md:text-2xl block">{t.subtitle1}</span>
                <span className="text-gray-500 dark:text-gray-400 text-lg md:text-xl block mt-1">{t.university}</span>
              </div>
            </div>

            <div className="mb-8 space-y-2 text-lg md:text-xl text-gray-600 dark:text-gray-300">
              <p>{t.subtitle2}</p>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-8 w-full px-6 md:px-0">
              
              {/* Wrap Nút Contact lại để gắn hiệu ứng tỏa ra và Tooltip */}
              <div className="relative w-full md:w-auto flex justify-center group">
                
                {/* Hiệu ứng tỏa ra (Ripple) tinh tế */}
                <div className="absolute inset-0 rounded-full ring-2 ring-blue-500 ring-opacity-50 animate-subtle-ripple pointer-events-none group-hover:opacity-0 transition-opacity duration-300 z-0"></div>

                <button 
                  onClick={() => {
                    handleCloseTour(); // Tắt tour ngay khi người dùng bấm nút
                    onOpenContact();
                  }}
                  className="w-full md:w-[200px] h-[52px] flex items-center justify-center bg-black dark:bg-black text-white rounded-full font-bold shadow-md hover:shadow-xl hover:scale-105 hover:bg-gray-800 transition-all duration-300 relative z-10"
                >
                  {t.contactBtn}
                </button>

                {/* Hộp thoại Tooltip CHỈ HIỆN KHI showTour = true - ĐÃ LÀM NHỎ GỌN LẠI */}
                {showTour && (
                  <div className="absolute top-full mt-4 w-52 md:w-60 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 p-3 md:p-4 z-50 animate-bounce">
                    {/* Mũi tên chĩa lên trên - size nhỏ hơn */}
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white dark:bg-gray-800 rotate-45 border-l border-t border-gray-100 dark:border-gray-700"></div>
                    
                    {/* Text size nhỏ gọn hơn, margin bottom bớt đi */}
                    <p className="text-xs md:text-sm text-gray-700 dark:text-gray-200 mb-3 text-center font-medium relative z-10 leading-relaxed">
                      {t.tourMessage}
                    </p>
                    
                    {/* Nút bấm mỏng hơn một chút */}
                    <button
                      onClick={handleCloseTour}
                      className="w-full py-1.5 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white text-xs md:text-sm font-bold rounded-lg transition-all shadow-md relative z-10"
                    >
                      {t.gotIt}
                    </button>
                  </div>
                )}
              </div>

              <div className="relative w-full md:w-[200px] h-[52px] flex items-center justify-center [&>*]:w-full [&>*]:h-full [&_button]:w-full [&_button]:h-full">
                <AnimatedBorderButton onClick={startSurvey}>
                  {t.viewProjects}
                </AnimatedBorderButton>
              </div>

              <div className="flex justify-center space-x-4 pt-2 md:pt-0 md:ml-2">
                {socialLinks.map((link, index) => (
                  <div key={index} className="relative group">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-full bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-700 dark:text-gray-300
                               hover:bg-blue-100 dark:hover:bg-blue-900 hover:text-blue-600 dark:hover:text-blue-300
                               hover:scale-110 transition-all duration-300 shadow-md hover:shadow-xl flex items-center justify-center"
                      aria-label={link.label}
                    >
                      {link.icon}
                    </a>
                    {/* Hover Tooltip (Black background, white text) */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 px-3 py-1.5 bg-black text-white text-xs font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-50 whitespace-nowrap shadow-xl">
                      {link.label}
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-black transform rotate-45"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
        </div>
      </div>

      {showScrollArrow && (
        <div className="absolute bottom-60 md:bottom-64 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-2 text-gray-600 dark:text-gray-300 z-20">
          <button
            onClick={scrollToAbout}
            className="flex flex-col items-center hover:text-blue-600 dark:hover:text-blue-400 transition-colors group"
            aria-label={t.scrollDown}
          >
            <span className="text-sm mb-2 group-hover:translate-y-1 transition-transform">{t.scrollDown}</span>
            <ChevronDown size={24} className="animate-bounce" />
          </button>
        </div>
      )}

      {/* Modal Khảo Sát Nằm Giữa Màn Hình - Đưa ra Portal để nằm trên cùng mọi thẻ */}
      {showProjectsSurvey && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Lớp nền làm mờ */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-md transition-opacity duration-300"
            onClick={() => setShowProjectsSurvey(false)}
          ></div>
          
          {/* Nội dung form Modal (Wider Rectangle) */}
          <div className="bg-white dark:bg-zinc-900 w-full max-w-xl md:max-w-2xl rounded-3xl shadow-2xl relative z-10 animate-popup-appear overflow-hidden border border-gray-100 dark:border-zinc-800 flex flex-col">
            
            {/* Header / Thanh tiến trình dạng đoạn */}
            <div className="p-5 md:px-10 md:pt-8 pb-3 flex flex-col gap-4 bg-white dark:bg-zinc-900">
              <div className="flex items-center">
                <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-blue-600 dark:text-blue-400">
                  {surveyStep === 1 ? t.step1Of2 : t.step2Of2}
                </span>
              </div>

              {/* Progress segments */}
              <div className="flex gap-2 w-full">
                <div className={`h-2 rounded-full flex-1 transition-all duration-500 ${surveyStep >= 1 ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-zinc-700'}`}></div>
                <div className={`h-2 rounded-full flex-1 transition-all duration-500 ${surveyStep >= 2 ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-zinc-700'}`}></div>
              </div>
            </div>

            {/* Nội dung Bước 1 */}
            {surveyStep === 1 && (
              <div className="px-6 pb-8 pt-4 md:px-12 md:pb-12 flex flex-col items-center text-center animate-[fade-in-up_0.3s_ease-out]">
                <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-6 text-blue-600 dark:text-blue-400 shadow-inner border border-blue-100 dark:border-blue-800/50 group">
                  <Compass size={40} className="group-hover:animate-spin-slow transition-transform" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 dark:text-white mb-4">
                  {t.surveyGreetingTitle}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-10 text-lg md:text-xl leading-relaxed max-w-lg">
                  {t.surveyGreetingDesc}
                </p>
                <div className="flex flex-col-reverse md:flex-row w-full md:w-4/5 gap-4">
                  <div className="flex-1 flex flex-col items-center gap-2">
                    <button 
                      onClick={() => setShowProjectsSurvey(false)}
                      className="w-full py-4 px-6 bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 font-bold rounded-2xl transition-colors text-lg"
                    >
                      {t.surveySkipBtn}
                    </button>
                    <span className="text-gray-400 dark:text-gray-500 text-sm animate-pulse">
                      {t.autoCloseMsg.replace('{n}', countdown.toString())}
                    </span>
                  </div>
                  <button 
                    onClick={() => setSurveyStep(2)}
                    className="flex-1 py-4 px-6 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-bold rounded-2xl transition-all shadow-lg hover:shadow-blue-500/30 flex items-center justify-center gap-2 text-lg h-[60px]"
                  >
                    <span>{t.surveyNextBtn}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 animate-pulse" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Nội dung Bước 2 */}
            {surveyStep === 2 && (
              <div className="px-6 pb-8 pt-4 md:px-12 md:pb-12 flex flex-col text-center animate-[fade-in-up_0.3s_ease-out]">
                <h3 className="text-2xl md:text-3xl font-bold font-sans text-gray-900 dark:text-white mb-3">
                  {t.surveyQuestionTitle}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-8 text-lg md:text-xl leading-relaxed">
                  {t.surveyQuestionDesc}
                </p>
                
                <div className="flex justify-center mb-8">
                  <button
                    onClick={scrollToFeatured}
                    className="w-full md:w-3/4 p-8 bg-white hover:bg-blue-50 dark:bg-zinc-900 dark:hover:bg-blue-900/20 text-gray-800 dark:text-gray-100 flex flex-col items-center justify-center font-bold rounded-2xl transition-all border-2 border-gray-100 hover:border-blue-500 dark:border-zinc-800 dark:hover:border-blue-500 group shadow-sm hover:shadow-xl"
                  >
                    <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:-translate-y-2 transition-transform duration-300 mb-4 shadow-sm">
                      <Rocket size={32} />
                    </div>
                    <div className="text-2xl text-center mb-1">{t.featuredOpt}</div>
                  </button>
                </div>

                <div className="w-full flex flex-col items-center gap-2">
                  <button 
                    onClick={() => setSurveyStep(1)}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white font-bold rounded-full flex items-center gap-2 transition-all"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    {t.surveyBackBtn}
                  </button>
                  <span className="text-gray-400 dark:text-gray-500 text-[13px] animate-pulse">
                    {t.autoCloseMsg.replace('{n}', countdown.toString())}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      , document.body)}

    </section>
  );
};

export default React.memo(Home);